/**
 * ObradorCreate (P-01 — create) — alta d'un obrador (tenant).
 *
 * Fields: nom comercial · raó social · NIF · sector · idioma · pla · email admin
 * inicial. Validated with the shared `TenantCreateInput` zod schema (single
 * source of truth, client + server). On submit we invoke `tenant-create`; the
 * server deploys the default structure and invites the admin (noted in the UI).
 * On success we navigate to the new tenant's detail screen.
 *
 * The zod schema is snake_case; the edge-function wire contract is camelCase, so
 * the validated payload is mapped to the request shape before invoking.
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Button, Card, Field, useTheme } from '@obrava/ui';
import { TenantCreateInput, type TenantCreateInput as TenantCreateInputType } from '@obrava/shared';
import { PageHeading, SectionTitle } from '../../components/layout.js';
import { Banner } from '../../components/Banner.js';
import { Select } from '../../components/Select.js';
import { sectorOptions, planOptions, languageOptions } from './labels.js';
import { useCreateTenant } from './data.js';
import type { TenantCreateRequest } from '../../lib/edge.js';

/** Map the validated (snake_case) input to the camelCase edge-function body. */
function toRequest(input: TenantCreateInputType): TenantCreateRequest {
  return {
    name: input.name,
    ...(input.legal_name ? { legalName: input.legal_name } : {}),
    ...(input.tax_id ? { taxId: input.tax_id } : {}),
    sector: input.sector,
    plan: input.plan,
    language: input.language,
    ...(input.admin_email ? { adminEmail: input.admin_email } : {}),
  };
}

export function ObradorCreate(): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const createTenant = useCreateTenant();
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TenantCreateInputType>({
    resolver: zodResolver(TenantCreateInput),
    defaultValues: {
      name: '',
      sector: 'generic',
      plan: 'obrador',
      language: 'ca',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const tenant = await createTenant.mutateAsync(toRequest(values));
      void navigate({ to: '/tenants/$id', params: { id: tenant.id } });
    } catch (err) {
      // useCreateTenant throws the edge function's stable error code as the
      // message. Map the known ones to a precise string; else a generic error.
      const code = err instanceof Error ? err.message : '';
      setSubmitError(
        code === 'tax_id_taken'
          ? t('platformConsole.create:taxIdTaken')
          : t('platformConsole.create:createError'),
      );
    }
  });

  const sectionGap = { display: 'flex', flexDirection: 'column', gap: theme.spacing.space[5] } as const;

  return (
    <div>
      <PageHeading
        title={t('platformConsole.create:title')}
        subtitle={t('platformConsole.create:subtitle')}
      />

      <form onSubmit={(e) => void onSubmit(e)} style={{ maxWidth: theme.spacing.size.contentMaxWidth }}>
        <Card style={{ marginBottom: theme.spacing.space[5] }}>
          <SectionTitle>{t('platformConsole.create:sectionFiscal')}</SectionTitle>
          <div style={sectionGap}>
            <Field
              label={t('platformConsole.create:name')}
              error={errors.name?.message}
              {...register('name')}
            />
            <Field
              label={t('platformConsole.create:legalName')}
              error={errors.legal_name?.message}
              {...register('legal_name')}
            />
            <Field
              label={t('platformConsole.create:taxId')}
              error={errors.tax_id?.message}
              {...register('tax_id')}
            />
          </div>
        </Card>

        <Card style={{ marginBottom: theme.spacing.space[5] }}>
          <SectionTitle>{t('platformConsole.create:sectionPlan')}</SectionTitle>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: theme.spacing.space[5],
            }}
          >
            <Select
              label={t('platformConsole.create:sector')}
              options={sectorOptions(t)}
              error={errors.sector?.message}
              {...register('sector')}
            />
            <Select
              label={t('platformConsole.create:plan')}
              options={planOptions(t)}
              error={errors.plan?.message}
              {...register('plan')}
            />
            <Select
              label={t('platformConsole.create:language')}
              options={languageOptions(t)}
              error={errors.language?.message}
              {...register('language')}
            />
          </div>
        </Card>

        <Card style={{ marginBottom: theme.spacing.space[5] }}>
          <SectionTitle>{t('platformConsole.create:sectionAdmin')}</SectionTitle>
          <div style={sectionGap}>
            <Field
              label={t('platformConsole.create:adminEmail')}
              type="email"
              error={errors.admin_email?.message}
              {...register('admin_email')}
            />
            <Banner tone="info">{t('platformConsole.create:deployNote')}</Banner>
          </div>
        </Card>

        {submitError ? (
          <Banner tone="danger" style={{ marginBottom: theme.spacing.space[5] }}>
            {submitError}
          </Banner>
        ) : null}

        <div style={{ display: 'flex', gap: theme.spacing.space[4] }}>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t('platformConsole.common:creating')
              : t('platformConsole.create:submit')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: '/tenants' })}
          >
            {t('platformConsole.create:cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
